import { GenderLabel } from '../dictionaries/gender-label.constant';
import { DateTime } from 'luxon';
import { selectPatientId } from 'src/app/store/patient/patient.selectors';
import { PatientInfoValueLabel } from './../dictionaries/patient-info-value-label.constant';
import { PatientInfoGroupLabel } from './../dictionaries/patient-info-group-label.constant';
import { PatientInfoGroupOrder } from './../dictionaries/patient-info-group-order.constant';
import { PatientInfoValueGroup } from './../dictionaries/patient-info-value-group.constant';
import { PatientInfoValueOrder } from './../dictionaries/patient-info-value-order.constant';
import { Update } from '@ngrx/entity';
import { AngularFirestore, DocumentReference } from '@angular/fire/firestore';
import { selectAllPatients, selectPatient } from 'src/app/store/patient/patient.selectors';
import { PatientState } from 'src/app/store/patient/patient.reducer';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, shareReplay, catchError, tap } from 'rxjs/operators';
import { Patient } from '../models/patient.model';
import { Observable } from 'rxjs';
import { keys } from 'lodash';
import { PatientInfoValues } from '../enums/patient-info-values.enum';
import { from } from 'rxjs';
import { deletePatient } from 'src/app/store/patient/patient.actions';
import { throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PatientService {

  patients$ = this.store.select(selectAllPatients).pipe(shareReplay(1));
  patient$ = this.store.select(selectPatient).pipe(shareReplay(1));
  patientId$ = this.store.select(selectPatientId).pipe(shareReplay(1));

  constructor(
    private store: Store<PatientState>,
    private afs: AngularFirestore
  ) { }

  addPatient(patient: Patient): Observable<DocumentReference> {
    return from(this.afs.collection('patients').add(patient));
  }

  updatePatient(update: Update<Patient>): Observable<void> {
    return from(this.afs.collection('patients').doc(update.id as string).update(update.changes));
  }

  deletePatient(id: string): Observable<void> {
    return from(this.afs.collection('patients').doc(id).delete()).pipe(
      catchError(error => throwError(error)),
      tap(() => this.store.dispatch(deletePatient({ id })))
    );
  }

  getPatientInfo(): Observable<any> {
    return this.patient$.pipe(
      map(patient => {
        if (patient) {
          return this.mapPatientToPatientInfo(patient);
        }
        return [];
      }),
      shareReplay(1)
    );
  }

  private mapPatientToPatientInfo(patient: Patient) {
    const patientInfoValues = keys(PatientInfoValueLabel).filter(key => patient[key]);
    return patientInfoValues.reduce((accumulator, currentValue) => {
      const currentValueGroup = PatientInfoValueGroup[currentValue];
      const currentValueOrder = PatientInfoValueOrder[currentValue];
      const groupLabel = PatientInfoGroupLabel[currentValueGroup];
      const groupOrder = PatientInfoGroupOrder[currentValueGroup];
      const groupIndex = accumulator.findIndex(value => value.label === groupLabel);
      if (groupIndex >= 0) {
        accumulator[groupIndex].value.splice(currentValueOrder, 0, this.getValueLabelFromValue(patient, currentValue));
      } else {
        accumulator.splice(groupOrder, 0, this.getGroupFromValue(patient, currentValue));
      }
      return accumulator;
    }, []);
  }

  private getValueLabelFromValue(patient: Patient, value: string) {
    let formattedValue = patient[value];

    if (value === PatientInfoValues.BIRTH_DATE) {
      formattedValue = this.getAgeFromBirthDate(patient[value]);
    }

    if (value === PatientInfoValues.WEIGHT) {
      formattedValue = `${patient[value]} kg`;
    }

    if (value === PatientInfoValues.GENDER) {
      formattedValue = GenderLabel[patient[value]];
    }

    return {
      label: PatientInfoValueLabel[value],
      value: formattedValue
    };
  }

  private getGroupFromValue(patient: Patient, value: string) {
    return {
      label: PatientInfoGroupLabel[PatientInfoValueGroup[value]],
      value: [this.getValueLabelFromValue(patient, value)]
    };
  }

  getAgeFromBirthDate(birthDate: any): string {
    const ageObject = this.getAgeObjectFromDate(birthDate.toDate());
    const ageString = [
      ...ageObject.years ? [this.getObjectKeyAsString(ageObject, 'years')] : [],
      ...ageObject.months ? [this.getObjectKeyAsString(ageObject, 'months')] : [],
      ...ageObject.days ? [this.getObjectKeyAsString(ageObject, 'days')] : []
    ].join(' ');
    return ageString;
  }

  private getObjectKeyAsString(object: any, key: string) {
    return object[key]
      ? object[key] === 1
        ? `${object[key]} ${key.slice(0, -1)}`
        : `${object[key]} ${key}`
      : null;
  }

  private getAgeObjectFromDate(birthDate: any): any {
    const d1 = DateTime.local();
    const d2 = DateTime.fromJSDate(birthDate);
    return d1.diff(d2, ['years', 'months', 'days', 'hours']).toObject();
  }
}
